import React, { useEffect, useState } from 'react';
import { Table, Progress } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BestSellingProduct, CategorySalesSummary, ReportFilter } from '../../model/report';
import { fetchProductReport } from '../../api/report';
import { exportExcel } from '../../utils/util';
import { useMeasuredRowScrollY, useTablePagination } from '../common/table-pagination';

interface Props {
    filter: ReportFilter;
}

const ReportProducts: React.FC<Props> = ({ filter }) => {
    const [bestSellers, setBestSellers] = useState<BestSellingProduct[]>([]);
    const [categorySales, setCategorySales] = useState<CategorySalesSummary[]>([]);
    const [loading, setLoading] = useState(false);
    // Both lists were `pagination={false}`: a shop with many categories,
    // or a long best-seller ranking, rendered every row and pushed the
    // rest of the tab off the screen.
    const categoryPagination = useTablePagination();
    const bestSellerPagination = useTablePagination();
    const { ref: categoryRef, y: categoryScrollY } = useMeasuredRowScrollY(420);
    const { ref: bestSellerRef, y: bestSellerScrollY } = useMeasuredRowScrollY(420);

    useEffect(() => {
        const load = async () => {
            setLoading(true);
            try {
                const res = await fetchProductReport(filter);
                if (res) {
                    setBestSellers(res.bestSellers || []);
                    setCategorySales(res.categorySales || []);
                }
            } catch (e) {
                console.error(e);
            }
            setLoading(false);
        };
        load();
    }, [filter]);

    const productColumns: ColumnsType<BestSellingProduct> = [
        {
            title: 'อันดับ',
            key: 'rank',
            width: 70,
            render: (_: any, __: any, index: number) => (
                <span style={{
                    fontWeight: 700,
                    color: index === 0 ? '#EAB308' : index === 1 ? '#94A3B8' : index === 2 ? '#B45309' : '#64748B',
                    fontSize: '15px'
                }}>
                    #{index + 1}
                </span>
            )
        },
        { title: 'รหัสสินค้า', dataIndex: 'product_code', key: 'product_code', width: 120 },
        {
            title: 'ชื่อสินค้า',
            dataIndex: 'product_name',
            key: 'product_name',
            render: (val: string) => <span style={{ fontWeight: 600, color: '#FF7A59' }}>{val}</span>
        },
        { title: 'หมวดหมู่', dataIndex: 'category_name', key: 'category_name' },
        {
            title: 'จำนวนที่ขายได้',
            dataIndex: 'quantity_sold',
            key: 'quantity_sold',
            render: (val: number, record: BestSellingProduct) => (
                <span style={{ fontWeight: 700 }}>{val} {record.unit_name}</span>
            )
        },
        {
            title: 'ยอดขายรวม (บาท)',
            dataIndex: 'total_sales',
            key: 'total_sales',
            render: (val: number) => <span style={{ fontWeight: 700, color: 'var(--primary, #FF7A59)' }}>฿{val.toLocaleString()}</span>
        },
        {
            title: 'กำไรขั้นต้น',
            dataIndex: 'profit',
            key: 'profit',
            render: (val: number) => <span style={{ color: '#03ffabfd', fontWeight: 600 }}>฿{val.toLocaleString()}</span>
        },
        {
            title: 'อัตรากำไร (%)',
            dataIndex: 'profit_margin',
            key: 'profit_margin',
            render: (val: number) => (
                <div style={{ width: 110 }}>
                    <Progress 
                        percent={val} 
                        size="small" 
                        strokeColor="#10B981" 
                        format={(percent) => <span style={{ color: '#4171ff', fontWeight: 700 }}>{percent}%</span>} 
                    />
                </div>
            )
        }
    ];

    const categoryColumns: ColumnsType<CategorySalesSummary> = [
        {
            title: 'หมวดหมู่สินค้า',
            dataIndex: 'category_name',
            key: 'category_name',
            render: (val: string) => <span style={{ fontWeight: 700 }}>{val}</span>
        },
        {
            title: 'จำนวนชิ้นที่ขายได้',
            dataIndex: 'total_quantity',
            key: 'total_quantity',
            render: (val: number) => `${val.toLocaleString()} ชิ้น`
        },
        {
            title: 'ยอดขายรวม (บาท)',
            dataIndex: 'total_sales',
            key: 'total_sales',
            render: (val: number) => <span style={{ fontWeight: 700, color: 'var(--primary, #FF7A59)' }}>฿{val.toLocaleString()}</span>
        },
        {
            title: 'สัดส่วนยอดขาย (%)',
            dataIndex: 'percentage',
            key: 'percentage',
            render: (val: number) => (
                <div style={{ width: 140 }}>
                    <Progress percent={val} size="small" strokeColor="var(--primary, #FF7A59)" />
                </div>
            )
        }
    ];

    const handleExportExcel = () => {
        const exportData = bestSellers.map((p, idx) => ({
            'อันดับ': idx + 1,
            'รหัสสินค้า': p.product_code,
            'ชื่อสินค้า': p.product_name,
            'หมวดหมู่': p.category_name,
            'จำนวนที่ขายได้': p.quantity_sold,
            'หน่วย': p.unit_name,
            'ยอดขายรวม': p.total_sales,
            'กำไรขั้นต้น': p.profit,
            'อัตรากำไร(%)': `${p.profit_margin}%`
        }));
        exportExcel('รายงานสินค้าขายดี', exportData);
    };

    return (
        <div>
            {/* Category Sales Breakdown Section */}
            <div className="report-card-section">
                <div className="report-section-header">
                    <span className="report-section-title">
                        <FontAwesomeIcon icon="layer-group" style={{ color: 'var(--primary, #FF7A59)' }} />
                        <span>ยอดขายแยกตามหมวดหมู่สินค้า (Sales by Category)</span>
                    </span>
                </div>
                <div ref={categoryRef}>
                <Table
                    columns={categoryColumns}
                    dataSource={categorySales}
                    rowKey="category_id"
                    pagination={categoryPagination}
                    scroll={{ y: categoryScrollY }}
                    loading={loading}
                />
                </div>
            </div>

            {/* Best Sellers Section */}
            <div className="report-card-section">
                <div className="report-section-header">
                    <span className="report-section-title">
                        <FontAwesomeIcon icon="fire" style={{ color: '#EF4444' }} />
                        <span>จัดอันดับสินค้าขายดี (Top Best Selling Products)</span>
                    </span>
                    <button
                        type="button"
                        className="btn-report-action btn-excel"
                        onClick={handleExportExcel}
                    >
                        <FontAwesomeIcon icon="file-excel" /> ส่งออก Excel
                    </button>
                </div>
                <div ref={bestSellerRef}>
                <Table
                    columns={productColumns}
                    dataSource={bestSellers}
                    rowKey="id"
                    pagination={bestSellerPagination}
                    scroll={{ y: bestSellerScrollY }}
                    loading={loading}
                />
                </div>
            </div>
        </div>
    );
};

export default ReportProducts;
